/**
 * Tickets API Service
 * Source: /Users/omarsabbah/Desktop/Messaging-Hub/docs/developer/ticketing/frontend-ticketing/api-reference.md
 *
 * All REST operations for the ticketing system.
 * Base URL: /api/tickets
 * Auth: Bearer token required on all requests
 * Tenant scoping: Automatic via printHouseId from JWT
 */

import { MessagingHubHttpClient } from '@/services/messaging-hub-http-client';
import {
  TicketFilters,
  PaginatedTicketsResponse,
  TicketWithDetails,
  CreateFromConversationDto,
  ReplyTicketResponse,
  UpdateTicketStatusDto,
  UpdateTicketPriorityDto,
  UpdateTicketDto,
  TicketUpdatedResponse,
  AssignTicketDto,
  AssignTicketResponse,
  BulkAssignTicketsDto,
  BulkAssignResponse,
  MergeTicketDto,
  ReopenTicketResponse,
  RelatedTicket,
  TicketHistoryEntry,
  AgentWorkload,
  GenericSuccessResponse,
  Provider,
  ProvidersResponse,
  ProviderChannel,
  ProviderChannelsResponse,
  Tag,
  AddTagsDto,
  CreateTagDto,
  UpdateTagDto,
  ListTagsQueryDto,
  GetPopularTagsQueryDto,
  TagsListResponse,
  TicketViewStatus,
  UnreadCountResponse,
} from '@/widgets/frontdesk/providers/tickets/types';

const TICKETS_BASE_URL = '/api/tickets';
const PROVIDERS_URL = '/api/providers';
const PROVIDER_CHANNELS_URL = '/api/provider-channels';
// TEMPORARY: disable all /api/tickets calls during testing.
// Set to false to re-enable real ticket API requests.
const TEMP_DISABLE_TICKETS_API = true;

export class TicketsService extends MessagingHubHttpClient {
  protected async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    const isTicketsEndpoint = normalizedEndpoint.startsWith(TICKETS_BASE_URL);

    if (TEMP_DISABLE_TICKETS_API && isTicketsEndpoint) {
      return this.getDisabledMockResponse<T>(normalizedEndpoint, options);
    }

    return super.request<T>(endpoint, options);
  }

  private getDisabledMockResponse<T>(endpoint: string, options: RequestInit): T {
    const method = `${options.method || 'GET'}`.toUpperCase();

    // List tickets
    if (endpoint === TICKETS_BASE_URL || endpoint.startsWith(`${TICKETS_BASE_URL}?`)) {
      return {
        tickets: [],
        pagination: null,
      } as T;
    }

    // Arrays endpoints
    if (
      endpoint.includes('/tags') ||
      endpoint.includes('/related') ||
      endpoint.includes('/history') ||
      endpoint.includes('/customer/') ||
      endpoint.includes('/dashboard/unassigned') ||
      endpoint.includes('/dashboard/workload')
    ) {
      return [] as T;
    }

    // Count endpoint
    if (endpoint.endsWith('/unread-count')) {
      return { count: 0 } as T;
    }

    // Generic status/update actions
    if (method !== 'GET') {
      return {
        success: true,
        message: 'Temporarily disabled (/api/tickets bypass)',
      } as T;
    }

    // Single ticket-like responses
    return {
      id: 'temp-disabled-ticket',
      ticketNumber: 'TEMP-0',
      conversations: [],
    } as T;
  }

  // ============================================
  // Core Ticket Lifecycle
  // ============================================

  /**
   * List Tickets with Filters and Pagination
   * GET /api/tickets?status=OPEN&limit=20
   */
  async listTickets(filters?: TicketFilters): Promise<PaginatedTicketsResponse> {
    const params = new URLSearchParams();

    if (filters?.search?.trim()) params.append('search', filters.search.trim());
    if (filters?.status) params.append('status', filters.status);
    if (filters?.priority) params.append('priority', filters.priority);
    if (filters?.assigneeId) params.append('assigneeId', filters.assigneeId);
    if (filters?.managerId) params.append('managerId', filters.managerId);
    if (filters?.categoryId) params.append('categoryId', filters.categoryId);
    if (filters?.createdBy) params.append('createdBy', filters.createdBy);
    if (filters?.customerEmail) params.append('customerEmail', filters.customerEmail);
    if (filters?.customerPhone) params.append('customerPhone', filters.customerPhone);
    if (filters?.providerChannel) params.append('providerChannel', filters.providerChannel);
    if (filters?.externalConversationId) params.append('externalConversationId', filters.externalConversationId);
    if (filters?.tags && filters.tags.length > 0) {
      filters.tags.forEach((tag) => params.append('tags', tag));
    }
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.sortBy) params.append('sortBy', filters.sortBy);
    if (filters?.sortOrder) params.append('sortOrder', filters.sortOrder);

    const queryString = params.toString() ? `?${params.toString()}` : '';
    return await this.get<PaginatedTicketsResponse>(`${TICKETS_BASE_URL}${queryString}`);
  }

  /**
   * Get Single Ticket by ID
   * GET /api/tickets/:id
   */
  async getTicketById(ticketId: string): Promise<TicketWithDetails> {
    return await this.get<TicketWithDetails>(`${TICKETS_BASE_URL}/${encodeURIComponent(ticketId)}`);
  }

  /**
   * Get Ticket by Conversation ID
   * Fetches a ticket associated with a Chatwoot/Email/WhatsApp conversation
   * GET /api/tickets/by-conversation/:conversationId
   *
   * @param conversationId - The conversation ID (Chatwoot conversation ID, Gmail threadId, etc.)
   * @returns The ticket if found
   * @throws Error if ticket not found (404)
   */
  async getTicketByConversationId(conversationId: string): Promise<TicketWithDetails> {
    return await this.get<TicketWithDetails>(`${TICKETS_BASE_URL}/by-conversation/${encodeURIComponent(conversationId)}`);
  }

  /**
   * Get Ticket by External Conversation ID (Legacy method)
   * @deprecated Use getTicketByConversationId() instead
   * Queries the backend to find a ticket associated with a Chatwoot/Email/WhatsApp conversation
   * GET /api/tickets?externalConversationId=:id&limit=1
   *
   * @param externalConversationId - The external conversation ID (Chatwoot conversation ID, Gmail threadId, etc.)
   * @returns The ticket if found, null if not found
   */
  async getTicketByExternalConversationId(externalConversationId: string): Promise<TicketWithDetails | null> {
    console.log('🔍 [TicketsService] Calling listTickets with filters:', {
      externalConversationId,
      limit: 1,
    });

    try {
      const response = await this.listTickets({
        externalConversationId,
        limit: 1,
      });

      console.log('📥 [TicketsService] listTickets response:', {
        totalTickets: response.tickets?.length || 0,
        hasTickets: response.tickets && response.tickets.length > 0,
        firstTicket: response.tickets?.[0]?.id || 'none'
      });

      if (response.tickets && response.tickets.length > 0) {
        return response.tickets[0];
      }

      return null;
    } catch (error) {
      console.error('❌ [TicketsService] listTickets failed:', error);
      throw error;
    }
  }

  /**
   * Create Ticket From Conversation
   * POST /api/tickets/create-from-conversation
   */
  async createTicketFromConversation(data: CreateFromConversationDto): Promise<TicketWithDetails> {
    return await this.post<TicketWithDetails>(`${TICKETS_BASE_URL}/create-from-conversation`, data);
  }

  /**
   * Reply to Ticket
   * POST /api/tickets/:id/reply
   *
   * BREAKING CHANGE: conversationId is NOW REQUIRED
   * Backend needs to know which conversation/channel to reply on (Email, WhatsApp, SMS, etc.)
   */
  async replyToTicket(ticketId: string, content: string, conversationId: string): Promise<ReplyTicketResponse> {
    return await this.post<ReplyTicketResponse>(
      `${TICKETS_BASE_URL}/${encodeURIComponent(ticketId)}/reply`,
      { content, conversationId }
    );
  }

  /**
   * Update Ticket (General Purpose)
   * PATCH /api/tickets/:id
   * Update one or more ticket fields in a single request
   * All fields are optional - only include what you want to update
   */
  async updateTicket(ticketId: string, data: UpdateTicketDto): Promise<TicketUpdatedResponse> {
    return await this.patch<TicketUpdatedResponse>(
      `${TICKETS_BASE_URL}/${encodeURIComponent(ticketId)}`,
      data
    );
  }

  /**
   * Update Ticket Status
   * PATCH /api/tickets/:id/status
   * @deprecated Use updateTicket() instead
   */
  async updateTicketStatus(ticketId: string, status: string): Promise<GenericSuccessResponse> {
    return await this.patch<GenericSuccessResponse>(
      `${TICKETS_BASE_URL}/${encodeURIComponent(ticketId)}/status`,
      { status }
    );
  }

  /**
   * Reopen Ticket (Manager/Admin)
   * POST /api/tickets/:id/reopen
   */
  async reopenTicket(ticketId: string): Promise<ReopenTicketResponse> {
    return await this.post<ReopenTicketResponse>(
      `${TICKETS_BASE_URL}/${encodeURIComponent(ticketId)}/reopen`
    );
  }

  /**
   * Update Ticket Priority (Manager/Admin)
   * PATCH /api/tickets/:id/priority
   * @deprecated Use updateTicket() instead
   */
  async updateTicketPriority(ticketId: string, priority: string): Promise<GenericSuccessResponse> {
    return await this.patch<GenericSuccessResponse>(
      `${TICKETS_BASE_URL}/${encodeURIComponent(ticketId)}/priority`,
      { priority }
    );
  }

  /**
   * Merge Tickets (Manager/Admin)
   * POST /api/tickets/:sourceId/merge
   */
  async mergeTickets(sourceId: string, targetTicketId: string): Promise<GenericSuccessResponse> {
    return await this.post<GenericSuccessResponse>(
      `${TICKETS_BASE_URL}/${encodeURIComponent(sourceId)}/merge`,
      { targetTicketId }
    );
  }

  // ============================================
  // Assignment & Work Management (Manager/Admin)
  // ============================================

  /**
   * Assign Ticket
   * POST /api/tickets/:id/assign
   */
  async assignTicket(ticketId: string, assigneeId: string): Promise<AssignTicketResponse> {
    return await this.post<AssignTicketResponse>(
      `${TICKETS_BASE_URL}/${encodeURIComponent(ticketId)}/assign`,
      { assigneeId }
    );
  }

  /**
   * Bulk Assign Tickets
   * POST /api/tickets/bulk-assign
   */
  async bulkAssignTickets(data: BulkAssignTicketsDto): Promise<BulkAssignResponse> {
    return await this.post<BulkAssignResponse>(`${TICKETS_BASE_URL}/bulk-assign`, data);
  }

  /**
   * Get Unassigned Tickets Dashboard
   * GET /api/tickets/dashboard/unassigned?priority=HIGH&categoryId=<id>
   */
  async getUnassignedTickets(options?: {
    priority?: string;
    categoryId?: string;
  }): Promise<TicketWithDetails[]> {
    const params = new URLSearchParams();

    if (options?.priority) params.append('priority', options.priority);
    if (options?.categoryId) params.append('categoryId', options.categoryId);

    const queryString = params.toString() ? `?${params.toString()}` : '';
    return await this.get<TicketWithDetails[]>(`${TICKETS_BASE_URL}/dashboard/unassigned${queryString}`);
  }

  /**
   * Get Agent Workload
   * GET /api/tickets/dashboard/workload
   */
  async getAgentWorkload(): Promise<AgentWorkload[]> {
    return await this.get<AgentWorkload[]>(`${TICKETS_BASE_URL}/dashboard/workload`);
  }

  // ============================================
  // Discovery & Insights
  // ============================================

  /**
   * Get Related Tickets
   * GET /api/tickets/:id/related
   */
  async getRelatedTickets(ticketId: string): Promise<RelatedTicket[]> {
    return await this.get<RelatedTicket[]>(
      `${TICKETS_BASE_URL}/${encodeURIComponent(ticketId)}/related`
    );
  }

  /**
   * Get Ticket History
   * GET /api/tickets/:id/history
   */
  async getTicketHistory(ticketId: string): Promise<TicketHistoryEntry[]> {
    return await this.get<TicketHistoryEntry[]>(
      `${TICKETS_BASE_URL}/${encodeURIComponent(ticketId)}/history`
    );
  }

  /**
   * Get Customer Ticket History
   * GET /api/tickets/customer/:identifier
   */
  async getCustomerTicketHistory(identifier: string): Promise<TicketWithDetails[]> {
    return await this.get<TicketWithDetails[]>(
      `${TICKETS_BASE_URL}/customer/${encodeURIComponent(identifier)}`
    );
  }

  // ============================================
  // Providers (Simplified API)
  // ============================================

  /**
   * Get All Providers for Organization
   * GET /api/providers
   * Returns all communication providers (Gmail accounts, WhatsApp numbers, SMS, etc.)
   */
  async getProviders(): Promise<Provider[]> {
    const response = await this.get<ProvidersResponse | Provider[]>(PROVIDERS_URL);

    // Handle both response formats:
    // 1. Wrapped: { data: [...] }
    // 2. Direct array: [...]
    if (Array.isArray(response)) {
      console.log('[TicketsService] Providers returned as direct array');
      return response;
    }

    if (response && typeof response === 'object' && 'data' in response) {
      console.log('[TicketsService] Providers returned wrapped in {data: [...]}');
      return response.data || [];
    }

    console.warn('[TicketsService] ⚠️ Unexpected response format from /providers:', response);
    return [];
  }

  /**
   * Get Single Provider by ID
   * GET /api/providers/:id
   */
  async getProviderById(providerId: string): Promise<Provider> {
    return await this.get<Provider>(
      `${PROVIDERS_URL}/${encodeURIComponent(providerId)}`
    );
  }

  // ============================================
  // Provider Channels (Legacy - Deprecated)
  // ============================================

  /**
   * Get All Provider Channels for Organization
   * GET /api/provider-channels
   * Returns all communication channels (email accounts, WhatsApp numbers, etc.)
   * @deprecated Use getProviders() instead
   */
  async getProviderChannels(): Promise<ProviderChannel[]> {
    const response = await this.get<ProviderChannelsResponse | ProviderChannel[]>(PROVIDER_CHANNELS_URL);

    // Handle both response formats:
    // 1. Wrapped: { data: [...] }
    // 2. Direct array: [...]
    if (Array.isArray(response)) {
      console.log('[TicketsService] Provider channels returned as direct array');
      return response;
    }

    if (response && typeof response === 'object' && 'data' in response) {
      console.log('[TicketsService] Provider channels returned wrapped in {data: [...]}');
      return response.data || [];
    }

    console.warn('[TicketsService] ⚠️ Unexpected response format from /provider-channels:', response);
    return [];
  }

  /**
   * Get Single Provider Channel by ID
   * GET /api/provider-channels/:id
   * @deprecated Use getProviderById() instead
   */
  async getProviderChannelById(channelId: string): Promise<ProviderChannel> {
    return await this.get<ProviderChannel>(
      `${PROVIDER_CHANNELS_URL}/${encodeURIComponent(channelId)}`
    );
  }

  // ============================================
  // Tag Management
  // ============================================

  /**
   * List All Tags (with optional filters)
   * GET /api/tickets/tags?category=PRIORITY&search=urgent
   */
  async listTags(query?: ListTagsQueryDto): Promise<Tag[]> {
    const params = new URLSearchParams();

    if (query?.category) params.append('category', query.category);
    if (query?.search) params.append('search', query.search);
    if (query?.page) params.append('page', query.page.toString());
    if (query?.limit) params.append('limit', query.limit.toString());

    const queryString = params.toString() ? `?${params.toString()}` : '';
    const response = await this.get<TagsListResponse | Tag[]>(`${TICKETS_BASE_URL}/tags${queryString}`);

    // Handle both response formats
    if (Array.isArray(response)) {
      return response;
    }
    return response.tags || [];
  }

  /**
   * Get Popular Tags
   * GET /api/tickets/tags/popular?limit=10
   */
  async getPopularTags(query?: GetPopularTagsQueryDto): Promise<Tag[]> {
    const params = new URLSearchParams();

    if (query?.category) params.append('category', query.category);
    if (query?.limit) params.append('limit', query.limit.toString());

    const queryString = params.toString() ? `?${params.toString()}` : '';
    return await this.get<Tag[]>(`${TICKETS_BASE_URL}/tags/popular${queryString}`);
  }

  /**
   * Suggest Tags (Autocomplete)
   * GET /api/tickets/tags/suggest?query=bu&limit=5
   */
  async suggestTags(query: string, limit?: number, category?: string): Promise<Tag[]> {
    const params = new URLSearchParams();
    params.append('query', query);
    if (limit) params.append('limit', limit.toString());
    if (category) params.append('category', category);

    return await this.get<Tag[]>(`${TICKETS_BASE_URL}/tags/suggest?${params.toString()}`);
  }

  /**
   * Create Tag (Admin/Manager)
   * POST /api/tickets/tags
   */
  async createTag(data: CreateTagDto): Promise<Tag> {
    return await this.post<Tag>(`${TICKETS_BASE_URL}/tags`, data);
  }

  /**
   * Update Tag (Admin/Manager)
   * PATCH /api/tickets/tags/:tagId
   */
  async updateTag(tagId: string, data: UpdateTagDto): Promise<Tag> {
    return await this.patch<Tag>(
      `${TICKETS_BASE_URL}/tags/${encodeURIComponent(tagId)}`,
      data
    );
  }

  /**
   * Delete Tag (Admin/Manager)
   * DELETE /api/tickets/tags/:tagId
   */
  async deleteTag(tagId: string): Promise<GenericSuccessResponse> {
    return await this.delete<GenericSuccessResponse>(
      `${TICKETS_BASE_URL}/tags/${encodeURIComponent(tagId)}`
    );
  }

  /**
   * Get Tags for a Ticket
   * GET /api/tickets/:id/tags
   */
  async getTicketTags(ticketId: string): Promise<Tag[]> {
    return await this.get<Tag[]>(
      `${TICKETS_BASE_URL}/${encodeURIComponent(ticketId)}/tags`
    );
  }

  /**
   * Add Tags to Ticket (auto-creates if needed)
   * POST /api/tickets/:id/tags
   */
  async addTagsToTicket(ticketId: string, tags: string[]): Promise<Tag[]> {
    return await this.post<Tag[]>(
      `${TICKETS_BASE_URL}/${encodeURIComponent(ticketId)}/tags`,
      { tags }
    );
  }

  /**
   * Remove Tag from Ticket
   * DELETE /api/tickets/:id/tags/:tagId
   */
  async removeTagFromTicket(ticketId: string, tagId: string): Promise<GenericSuccessResponse> {
    return await this.delete<GenericSuccessResponse>(
      `${TICKETS_BASE_URL}/${encodeURIComponent(ticketId)}/tags/${encodeURIComponent(tagId)}`
    );
  }

  // ============================================
  // Ticket View Tracking (Read/Unread Status)
  // ============================================

  /**
   * Get Ticket View Status for Current User
   * GET /api/tickets/:id/view-status
   * Returns when the user last viewed this ticket and if there's unread activity
   */
  async getTicketViewStatus(ticketId: string): Promise<TicketViewStatus> {
    return await this.get<TicketViewStatus>(
      `${TICKETS_BASE_URL}/${encodeURIComponent(ticketId)}/view-status`
    );
  }

  /**
   * Mark Ticket as Read
   * PATCH /api/tickets/:id/mark-read
   * Manually mark a ticket as read (updates lastViewedAt and sets hasUnread to false)
   */
  async markTicketAsRead(ticketId: string): Promise<GenericSuccessResponse> {
    return await this.patch<GenericSuccessResponse>(
      `${TICKETS_BASE_URL}/${encodeURIComponent(ticketId)}/mark-read`,
      {}
    );
  }

  /**
   * Get Unread Tickets Count for Current User
   * GET /api/tickets/unread-count
   * Returns the number of tickets with unread activity for the current user
   */
  async getUnreadTicketsCount(): Promise<UnreadCountResponse> {
    return await this.get<UnreadCountResponse>(`${TICKETS_BASE_URL}/unread-count`);
  }
}

// Export singleton instance
export const ticketsService = new TicketsService();
